import { useMemo } from 'react';
import { EventName, EventParams, FilterSections, FiltersValues } from '../types';
import { useAnalyticsContext } from '../analytics/analytics.provider';
import { ChosenCategoryType } from '../analytics/types';

export const GLOBAL_FILTER_NAME = 'Global Filter';

export const useFilterTracking = (sections: FilterSections[]) => {
  const { trackPageEvent } = useAnalyticsContext();

  const { labelLookup, groupLabelLookup } = useMemo(() => getLabelLookups(sections), [sections]);

  const trackFilterApplied = (filterValues: FiltersValues) => {
    const chosenCategories = getChosenCategories(filterValues, labelLookup);
    const eventParams = getEventParams(filterValues, chosenCategories, groupLabelLookup);

    if (eventParams === null) {
      return;
    }

    trackPageEvent(EventName.FilterApplied, eventParams);

    // for each category chosen, send filter applied details event, distinguishing between premium and non-premium filters
    chosenCategories.forEach(category => {
      const { key } = category;
      const eventParamsFilterDetails: EventParams = getFilterDetails(category, eventParams, !!groupLabelLookup[key]);
      trackPageEvent(EventName.FilterAppliedDetails, eventParamsFilterDetails);
    });
  };

  return { trackFilterApplied };
};

const getFilterDetails = (category: ChosenCategoryType, eventParams: EventParams, isPremium: boolean) => {
  const { label: categoryChosen, values: optionChosen } = category;
  // groupLabelLookup is a lookup table for premium filters

  const { filterSection, ...eventParamsWithoutFilterSection } = eventParams;

  return {
    ...eventParamsWithoutFilterSection,
    ...(!isPremium ? {} : { filterSection }),
    categoryChosen,
    optionChosen,
  };
};

const getAllDefinitions = (sections: FilterSections[]) =>
  sections.reduce((acc, section) => {
    return [
      ...acc,
      ...(section.ungrouped?.definitions || []),
      ...(section.grouped?.flatMap(group => group.definitions) || []),
    ];
  }, []);

const getLabelLookup = (sections: FilterSections[]) => {
  const allDefinitions = getAllDefinitions(sections);
  return allDefinitions.reduce((acc, current) => ({ ...acc, [current.name]: current.label }), {});
};

const getGroupLabels = (sections: FilterSections[]): { groupLabel: string; name: string }[] =>
  sections.reduce((acc, section) => {
    return [
      ...acc,
      ...(section.grouped?.flatMap(group =>
        group.definitions.map(def => ({ groupLabel: group.label, name: def.name }))
      ) || []),
    ];
  }, []);

const getGroupLabelLookup = (sections: FilterSections[]) => {
  const groupLabels = getGroupLabels(sections);
  return groupLabels.reduce((acc, current) => ({ ...acc, [current.name]: current.groupLabel }), {});
};

export const getLabelLookups = (sections: FilterSections[]) => {
  const labelLookup = getLabelLookup(sections);
  const groupLabelLookup = getGroupLabelLookup(sections);
  return { labelLookup, groupLabelLookup };
};

export const getEventParams = (
  filterValues: FiltersValues,
  chosenCategories: { label: string; key: string; values: string[] }[],
  groupLabelLookup: Record<string, string>
) => {
  const nonEmptyFilterKeys = Object.keys(filterValues).filter(key => filterValues[key].length > 0);

  if (nonEmptyFilterKeys.length === 0) {
    return null;
  }

  const filterSection = [...new Set(nonEmptyFilterKeys.map(key => groupLabelLookup[key]).filter(Boolean))];

  const categoryCombination = chosenCategories.map(category => category.label);

  const eventParams: EventParams = {
    categoryCombination,
    filterName: GLOBAL_FILTER_NAME,
    filterType: 'Platform',
  };

  if (filterSection.length) {
    eventParams.filterSection = filterSection;
  }

  return eventParams;
};

export const getChosenCategories = (
  filterValues: FiltersValues,
  labelLookup: Record<string, string>
): ChosenCategoryType[] => {
  const nonEmptyFilterKeys = Object.keys(filterValues).filter(key => filterValues[key].length > 0);

  return nonEmptyFilterKeys.map(key => ({
    key,
    label: labelLookup[key],
    values: filterValues[key].map(value => value.label),
  }));
};
